import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

export const NZ_TIMEZONE = 'Pacific/Auckland';

const NZ_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Convert an ISO8601 string (stored in UTC) to a Date object that represents
 * the same calendar day in the Pacific/Auckland timezone. The returned Date
 * is created in the local environment timezone but retains just the Y-M-D
 * portion so Mantine's DatePicker can display it without shifting days.
 */
export const isoToNzCalendarDate = (isoValue: string): Date | null => {
  if (!isoValue) {
    return null;
  }

  const utcDate = new Date(isoValue);
  if (Number.isNaN(utcDate.getTime())) {
    return null;
  }

  const zoned = utcToZonedTime(utcDate, NZ_TIMEZONE);
  const calendarDate = new Date(
    zoned.getFullYear(),
    zoned.getMonth(),
    zoned.getDate(),
    0,
    0,
    0,
    0,
  );

  return calendarDate;
};

/**
 * Convert a Date selected in the UI to a UTC ISO string representing the
 * midnight that begins that calendar day in the Pacific/Auckland timezone.
 */
export const nzCalendarStartOfDayIso = (date: Date): string => {
  const nzDateString = formatInTimeZone(date, NZ_TIMEZONE, NZ_DATE_FORMAT);
  const utcDate = zonedTimeToUtc(`${nzDateString}T00:00:00`, NZ_TIMEZONE);
  return utcDate.toISOString();
};

/**
 * Convert a Date selected in the UI to a UTC ISO string representing the final
 * millisecond of that calendar day in the Pacific/Auckland timezone. Useful
 * when the date should be treated as inclusive.
 */
export const nzCalendarEndOfDayIso = (date: Date): string => {
  const nzDateString = formatInTimeZone(date, NZ_TIMEZONE, NZ_DATE_FORMAT);
  const utcDate = zonedTimeToUtc(`${nzDateString}T23:59:59.999`, NZ_TIMEZONE);
  return utcDate.toISOString();
};

/**
 * Compare two calendar dates as Pacific/Auckland midnights.
 * Returns negative if start < end, zero if equal, positive if start > end.
 */
export const compareNzCalendarDates = (start: Date, end: Date): number => {
  const startIso = nzCalendarStartOfDayIso(start);
  const endIso = nzCalendarStartOfDayIso(end);
  return new Date(startIso).getTime() - new Date(endIso).getTime();
};

/**
 * Format a stored ISO date (UTC) into a human-friendly label using the
 * Pacific/Auckland timezone.
 */
export const formatNzDateLabel = (
  isoValue: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  if (!isoValue) {
    return '';
  }

  return formatInTimeZone(isoValue, NZ_TIMEZONE, buildFormatString(options));
};

/**
 * Build date-fns format tokens based on Intl options so we can leverage
 * formatInTimeZone without duplicating option parsing.
 */
const buildFormatString = (options: Intl.DateTimeFormatOptions) => {
  const tokens: string[] = [];

  switch (options.day) {
    case 'numeric':
      tokens.push('d');
      break;
    case '2-digit':
      tokens.push('dd');
      break;
    default:
      break;
  }

  switch (options.month) {
    case 'numeric':
      tokens.push('M');
      break;
    case '2-digit':
      tokens.push('MM');
      break;
    case 'short':
      tokens.push('MMM');
      break;
    case 'long':
      tokens.push('MMMM');
      break;
    default:
      break;
  }

  switch (options.year) {
    case 'numeric':
      tokens.push('yyyy');
      break;
    case '2-digit':
      tokens.push('yy');
      break;
    default:
      break;
  }

  if (tokens.length === 0) {
    tokens.push('dd MMM yyyy');
  }

  return tokens.join(' ');
};

/**
 * Format a start/end ISO pair into a NZT range label.
 */
export const formatNzDateRange = (startIso: string, endIso: string): string => {
  if (!startIso || !endIso) {
    return 'Date pending';
  }

  try {
    const startYear = formatInTimeZone(startIso, NZ_TIMEZONE, 'yyyy');
    const endYear = formatInTimeZone(endIso, NZ_TIMEZONE, 'yyyy');
    const startFormat = startYear === endYear ? 'dd MMM' : 'dd MMM yyyy';

    const startLabel = formatInTimeZone(startIso, NZ_TIMEZONE, startFormat);
    const endLabel = formatInTimeZone(endIso, NZ_TIMEZONE, 'dd MMM yyyy');

    return `${startLabel} - ${endLabel}`;
  } catch {
    return 'Date pending';
  }
};
